import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as LanguageAPI from 'api/Language';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  pagination: {
    pageSize: 10,
    currentPage: 1,
    totalRecords: null
  },
  column: [
    { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Code', key: 'code', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Scope', key: 'scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'STT Provider', key: 'stt_provider', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'URL', key: 'stt_data.url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Encoding', key: 'stt_data.encoding', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Language Code', key: 'stt_data.languageCode', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'LLM Language Code', key: 'llm_language_code', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Audio Socket URL', key: 'audio_socket_url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Call Produce Topic Name', key: 'call_produce_topic_name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'LLM Translate', key: 'llm_translate', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
    { title: 'Subscription Plans', key: 'subscription_plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
    { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
  ],
  data: null,
  customerLanguage: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'agent_core_language.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Code', key: 'agent_core_language.code', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Scope', key: 'agent_core_language.scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'STT Provider', key: 'agent_core_language.stt_provider', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'URL', key: 'agent_core_language.stt_data.url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Encoding', key: 'agent_core_language.stt_data.encoding', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Language Code', key: 'agent_core_language.stt_data.languageCode', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'LLM Language Code', key: 'agent_core_language.llm_language_code', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Audio Socket URL', key: 'agent_core_language.audio_socket_url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      {
        title: 'Call Produce Topic Name',
        key: 'agent_core_language.call_produce_topic_name',
        field_type: TYPE_OF_FIELD_IN_COLUMN.STRING,
        width: 120
      },
      { title: 'LLM Translate', key: 'agent_core_language.llm_translate', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
      {
        title: 'Subscription Plans',
        key: 'agent_core_language.subscription_plan_type',
        field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY,
        width: 120
      },
      { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPublicLanguages: []
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getLanguageData = createAsyncThunk('language/getLanguageData', async ({ params, signal }) => {
  const res = await LanguageAPI.getLanguageData(params, signal);
  return res?.data?.data;
});

const createLanguage = createAsyncThunk('language/createLanguage', async (params) => {
  try {
    const res = await LanguageAPI.createLanguage(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateLanguage = createAsyncThunk('language/updateLanguage', async (params) => {
  try {
    const res = await LanguageAPI.updateLanguage(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteLanguage = createAsyncThunk('language/deleteLanguage', async (id) => {
  try {
    const res = await LanguageAPI.deleteLanguage(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Customer
const getCustomerLanguageData = createAsyncThunk('language/getCustomerLanguageData', async ({ params, signal }) => {
  const res = await LanguageAPI.getCustomerLanguageData(params, signal);
  return res?.data?.data;
});

const createCustomerLanguage = createAsyncThunk('language/createCustomerLanguage', async (params) => {
  try {
    const res = await LanguageAPI.createCustomerLanguage(params);
    displayMessage({ type: 'success', msg: 'Attached successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateCustomerLanguage = createAsyncThunk('language/updateCustomerLanguage', async (params) => {
  try {
    const res = await LanguageAPI.updateCustomerLanguage(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomerLanguage = createAsyncThunk('language/deleteCustomerLanguage', async (id) => {
  try {
    const res = await LanguageAPI.deleteCustomerLanguage(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPublicLanguages = createAsyncThunk('language/getAllPublicLanguages', async () => {
  const res = await LanguageAPI.getAllPublicLanguages();
  return res?.data?.data;
});

const LanguageSlice = createSlice({
  name: 'language',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setLanguageCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setLanguagePageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },

    //Customer
    setCustomerLanguageCurrentPage: (state, action) => {
      state.customerLanguage.pagination.currentPage = action.payload;
    },
    setCustomerLanguagePageSize: (state, action) => {
      state.customerLanguage.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getAllPublicLanguages.fulfilled]: (state, action) => {
      state.customerLanguage.allPublicLanguages = action.payload;
    },
    [getLanguageData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getLanguageData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.pagination.totalRecords = action.payload?.count;
      state.data = action.payload?.results || [];
    },
    [getLanguageData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateLanguage.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.data = updatedData;
    },

    [updateLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getCustomerLanguageData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerLanguageData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerLanguage.pagination.totalRecords = action.payload?.count;
      state.customerLanguage.data = action.payload?.results || [];
    },
    [getCustomerLanguageData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomerLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomerLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomerLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomerLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomerLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomerLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomerLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const { resetState, setLanguageCurrentPage, setLanguagePageSize, setCustomerLanguageCurrentPage, setCustomerLanguagePageSize } =
  LanguageSlice.actions;

export default LanguageSlice.reducer;

export {
  resetState,
  setLanguageCurrentPage,
  setLanguagePageSize,
  getLanguageData,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  //Customer
  setCustomerLanguageCurrentPage,
  setCustomerLanguagePageSize,
  getAllPublicLanguages,
  getCustomerLanguageData,
  createCustomerLanguage,
  updateCustomerLanguage,
  deleteCustomerLanguage
};
